import { useCallback } from 'react'

import qs from '@scoir/query-string'
import window from 'browser-lib/window'
import cookie from 'cookie'
import { filter, forEach, get, isEmpty, isUndefined, keys } from 'lodash-es'

import api, { jsonOptions } from 'common/api'
import { getTrackEventsSessionId } from 'common/get-track-events-session-id'
import { useClearClickTrackingQueryParams, useClickTrackingQueryParams } from 'components/QueryParamsProvider'
import { ChallengeData } from 'components/SignIn/SignInForm.types'

import { COBRAND_ALIAS } from './coalition-sources'

const ROUTE_MAP = {
  Student: '/student/home?interrupt=1',
  Parent: '/parent/home?interrupt=1',
  Counselor: '/counselor/home',
  Advisor: '/guidance/home',
  Admin: '/admin/default',
  AdmissionsOfficer: '/admissions/home',
  CMS: '/admin/cms',
  Teacher: undefined,
} as const

const COOKIE_PATHS_BY_USER_TYPE = {
  Admin: '/admin',
  CMS: '/admin',
  AdmissionsOfficer: '/admissions',
  Counselor: '/counselor',
  Advisor: '/guidance',
  Parent: '/parent',
  Student: '/student',
  Teacher: '/teacher',
} as const

type UserType = keyof typeof COOKIE_PATHS_BY_USER_TYPE

export const clearLocalStorage = (userType: UserType) => {
  const storageKeys = keys(window.localStorage)
  const filtered = filter(storageKeys, (storageKey) => {
    let type = 'admissions'
    if (userType !== 'AdmissionsOfficer') {
      type = userType.toLowerCase()
    }
    const key = `^scoir-${type}-.+`
    const regex = new RegExp(key)
    return regex.test(storageKey)
  })

  forEach(filtered, (key) => {
    window.localStorage.removeItem(key)
  })
}

export const setCookies = (userType: UserType, accessToken: string) => {
  document.cookie = cookie.serialize('bearer', accessToken, {
    path: COOKIE_PATHS_BY_USER_TYPE[userType],
  })
  document.cookie = cookie.serialize('bearer', accessToken, {
    path: '/api/oauth2/authorize',
  })
  document.cookie = cookie.serialize('lastLoginType', userType, { path: '/' })
}

export const routeToApp = (userType: UserType, params?: Record<string, string>) => {
  const location = ROUTE_MAP[userType]
  let routeUrl = isUndefined(location) ? '/' : location
  const paramString = qs.stringify(params)
  const alreadyHasParams = routeUrl.includes('?')
  if (!isEmpty(params)) routeUrl += paramString ? `${alreadyHasParams ? '&' : '?'}${paramString}` : ''
  window.location.href = routeUrl
}

export const performLogin = (
  data: loginSuccessData,
  routeToAppArgs: { referralScid?: string; referralSource?: string },
  onMultiFactorCheck?: (data: ChallengeData) => void,
  onSuccess?: () => void,
  onPasswordChangeRequired?: () => void,
) => {
  if (data && data.UserTypes && data.UserTypes.length > 0) {
    const userType = data.UserTypes[0]
    if (data.PasswordChangeRequired) {
      onPasswordChangeRequired?.()
    } else if (onMultiFactorCheck && data?.ChallengeId) {
      onMultiFactorCheck({ UserId: data.UserId, ChallengeId: data.ChallengeId })
    } else {
      clearLocalStorage(userType)
      if (data.access_token) {
        setCookies(userType, data.access_token)
      }
      if (onSuccess) {
        onSuccess()
      } else {
        routeToApp(userType, routeToAppArgs)
      }
    }
  }
}

export type loginSuccessData = {
  UserId: string
  UserTypes?: ('Admin' | 'CMS' | 'AdmissionsOfficer' | 'Counselor' | 'Advisor' | 'Parent' | 'Student' | 'Teacher')[]
  access_token?: string
  ChallengeId?: string
  PasswordChangeRequired?: boolean
}
export const useLogin = (
  onSuccess?: () => void,
  onMultiFactorCheck?: (data: ChallengeData) => void,
  onPasswordChangeRequired?: () => void,
) => {
  const clickTrackingQueryParams = useClickTrackingQueryParams()
  const onClearClickTrackingQueryParams = useClearClickTrackingQueryParams()

  return useCallback(
    (userName, password, parameters = '') => {
      const credentials = {
        UserName: userName,
        Password: password,
      }

      const parsedParams = qs.parse(parameters)
      const params = qs.stringify({
        ...clickTrackingQueryParams,
        ...parsedParams,
        sessionId: getTrackEventsSessionId(),
      })
      return api
        .post<{ UserName: string; Password: string }, loginSuccessData | undefined>(
          `/api/login?${params}`,
          credentials,
          jsonOptions,
        )
        .then((data) => {
          if (data && data.UserTypes && data.UserTypes.length > 0) {
            const routeToAppArgs = {
              referralScid: parsedParams.scid,
              referralSource: get(COBRAND_ALIAS, parsedParams.source, parsedParams.source),
            }
            performLogin(data, routeToAppArgs, onMultiFactorCheck, onSuccess, onPasswordChangeRequired)
          } else {
            return Promise.resolve({
              data: 'This account does not have any active roles and cannot login.',
            })
          }
          onClearClickTrackingQueryParams()
        })
        .catch((errorResponse) => Promise.reject(errorResponse))
    },
    [
      clickTrackingQueryParams,
      onClearClickTrackingQueryParams,
      onSuccess,
      onMultiFactorCheck,
      onPasswordChangeRequired,
    ],
  )
}
